import { FunctionComponent, PropsWithChildren, ReactNode } from 'react';

import { RffInput, RffInputProps } from '@cast/shared-rff-ui';

import { InfoTooltip, Stack, tokens } from '@cast/design-system';

const getVariantPropsBase = (
  variant: NonNullable<PolicyInputProps['variant']>
) => {
  const variantProps: Record<
    NonNullable<PolicyInputProps['variant']>,
    Partial<RffInputProps>
  > = {
    percentage: {
      min: 0,
      max: 100,
      endAdornment: '%',
    },
    percentile: {
      min: 0,
      max: 100,
      startAdornment: 'p',
    },
    days: {
      withSpinButtons: true,
      min: 0,
      max: 7,
      endAdornment: 'd',
    },
    hours: {
      withSpinButtons: true,
      min: 0,
      max: 23,
      endAdornment: 'h',
    },
    min: {
      withSpinButtons: true,
      min: 1,
      max: 60,
      endAdornment: 'min',
    },
    sec: {
      withSpinButtons: true,
      min: 0,
      max: 59,
      endAdornment: 'sec',
    },
    number: {
      withSpinButtons: true,
    },
    default: {},
  };
  return variantProps[variant];
};

export type PolicyInputProps = {
  variant?:
    | 'percentage'
    | 'percentile'
    | 'days'
    | 'hours'
    | 'min'
    | 'sec'
    | 'number'
    | 'default';
  LabelComponent?: FunctionComponent<PropsWithChildren>;
  infoTooltip?: string | null;
  labelTooltip?: ReactNode;
  hintAbsolute?: boolean;
} & RffInputProps;

export const PolicyInput = ({
  variant = 'default',
  LabelComponent,
  label,
  infoTooltip,
  labelTooltip,
  hintAbsolute = true,
  ...props
}: PolicyInputProps) => {
  const variantProps = getVariantPropsBase(variant);

  const labelNode = labelTooltip ? (
    <Stack direction="row" alignItems="center" gap={4} component="span">
      {label}
      <InfoTooltip title={labelTooltip} iconSize="15px" />
    </Stack>
  ) : (
    label
  );

  return (
    <RffInput
      type="number"
      size="small"
      fullWidth
      {...variantProps}
      {...props}
      label={
        LabelComponent ? (
          <LabelComponent>{labelNode}</LabelComponent>
        ) : (
          labelNode
        )
      }
      hintAbsolute={hintAbsolute}
      endAdornment={
        infoTooltip ? (
          <Stack gap={4} direction="row" alignItems="center">
            {variantProps.endAdornment || props.endAdornment}
            <InfoTooltip
              title={infoTooltip}
              placement="right"
              sx={{ color: tokens.theme.fg.structural.secondary }}
            />
          </Stack>
        ) : (
          variantProps.endAdornment || props.endAdornment
        )
      }
    />
  );
};
